import AttachFileIcon from '@mui/icons-material/AttachFile';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { StatusCodes } from 'http-status-codes';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import useEncryption from '../../hooks/cryptography/encryptText';
import StandardTextEditor from '../common/editor/standardTextEditor';
import { PendingFilesObject } from '../customer/report/reportDocuments/attachments/messages';
import PendingAttachments from '../customer/report/reportDocuments/attachments/messages/PendingAttachments';
import ExpandButton from '../reportSubmission/fullscreenDescription/buttons/expandButton';
import DescriptionDialog from '../reportSubmission/fullscreenDescription/dialog/descriptionDialog';

import '../../assets/styles/formattedTextViewer.css';
import { getFileExtension } from '~/assets/utils/helpers';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import { FileUploadService } from '~/services/api/file-upload/endpoint';
import { isMutationSuccess } from '~/services/api/rest-api';
import { isMutationError } from '~/services/api/rest-api-error';

interface WSSendMessage {
  message: string;
  to_user?: number;
  action: string;
  file_ids: number[];
}

interface ChatboxProps {
  disabled: boolean;
  onSendMessage: (chat: WSSendMessage) => void;
  conversationId: string;
}

const Chatbox = ({ disabled, onSendMessage, conversationId }: ChatboxProps) => {
  const MAX_FILE_SIZE = 26214400;
  const [text, setText] = useState('');
  const [pendingFiles, setPendingFiles] = useState<PendingFilesObject[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [openDialog, setOpen] = useState(false);
  const intl = useIntl();
  const { encryptText } = useEncryption();
  const { createToastNotification } = useToastNotificationContext();

  const [uploadAttachment] = FileUploadService.endpoints.uploadMessageAttachmentProcess.useMutation();
  const [addedPendingFiles, setAddedPendingFiles] = useState<boolean>(false);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState<boolean>(false);
  const [isErrorOnUpload, setIsErrorOnUpload] = useState<{ isError: boolean, message: string }>({
    isError: false,
    message: '',
  });

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
  };

  const isTextEmpty = () => {
    return text.replace(/<[^>]*>?/gm, '').trim().length === 0;
  };

  const textChangeHandler = (content: string) => {
    setText(content);
  };

  const sendMessageHandler = () => {
    // Description in-transit encryption preparation
    encryptText(text).then((encryptedText) => {
      const chat: WSSendMessage = {
        message: encryptedText,
        file_ids: pendingFiles.filter(file => file.status === 'success' && file.id !== undefined).map(file => file.id!),
        action: 'create',
      };
      onSendMessage(chat);
    });

    handleCloseDialog();
    setText('');
    setPendingFiles([]);
  };


  const uploadFile = useCallback(async (file: File) => {
    try {
      // Upload file and get response
      const res = await uploadAttachment({
        conversationId: conversationId,
        file: file,
        startUploadData: {
          file_name: file.name,
          file_type: getFileExtension(file.name),
          file_size: file.size,
        },
      });
      // Check if upload was successful
      if (isMutationSuccess(res) && res.data.id) {
        return {
          id: res.data.id,
          file: file,
          status: 'success',
        };
      } else {
        let status = 'failed';
        if (isMutationError(res) && res.error.status === StatusCodes.FORBIDDEN) {
          status = 'no_retry';

          createToastNotification(
            res.error.data.message,
            'error',
          );
        }

        return {
          file: file,
          status: status,
        };
      }
    } catch (error) {
      return {
        file: file,
        status: 'failed',
      };
    }
  }, [conversationId, uploadAttachment, createToastNotification]);

  const uploadAllFiles = useCallback(async () => {
    setIsUploadingAttachment(true);
    const files = [...pendingFiles];
    const promises = files.map(async (file) => {
      if (['loading', 'failed', 'no_retry'].includes(file.status)) {
      const result = await uploadFile(file.file);
        return {
          ...file,
          ...result,
        };
      }
      return file;
    });

    // Execute all upload promises
    const updatedFiles = await Promise.all(promises);
    const validUpdatedFiles = updatedFiles.filter((file) => file.status !== 'no_retry');  // prevents retry of invalid files
    setAddedPendingFiles(false);
    setPendingFiles(validUpdatedFiles);
    setIsUploadingAttachment(false);
  }, [pendingFiles, uploadFile]);

  const handleChooseFileClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click(); // Trigger click event on the input
    }
  };

  const isFileSizeValidated = (files: File[]): boolean => {
    let isError: boolean = false;
    files.map(file => {
      if (file.size >= MAX_FILE_SIZE) {
        isError = true;
      }
    });
    if (isError) {
      setIsErrorOnUpload({
        isError: true,
        message: intl.formatMessage({
          defaultMessage: 'The files you have selected is too large. The maximum size is 25MB. Compress your files to reduce its size.',
        }),
      });
      return false;
    }
    setIsErrorOnUpload({
      isError: false,
      message: '',
    });
    return true;
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const newFiles = [...files];
    if (!isFileSizeValidated(newFiles)) return;
    setPendingFiles(prevPendingFiles => [
        ...prevPendingFiles,
        ...newFiles.map(file => ({
          file: file,
          status: 'loading',
        })),
      ]);
      setAddedPendingFiles(true);
  };

  useEffect(() => {
    if (addedPendingFiles)
      uploadAllFiles();
  }, [addedPendingFiles, uploadAllFiles]);

  const removePendingFile = (key: number) => {
    const files = [...pendingFiles];
    files.splice(key, 1);
    setPendingFiles(files);
  };

  const SendButton = () => (
    <Stack sx={{
      flexDirection: 'row',
      alignItems: 'center',
      marginLeft: 'auto',

    }}>
      <IconButton onClick={handleChooseFileClick} disabled={isUploadingAttachment}>
        <AttachFileIcon/>
      </IconButton>
      <input
        id='file-upload'
        type='file'
        style={{ display: 'none' }}
        multiple
        onChange={handleFileChange}
        ref={fileInputRef}
      />
      <Button
        onClick={sendMessageHandler}
        sx={{
          width: '6.25rem',
          backgroundColor: '#2449FC',
          color: 'white',
          borderRadius: '33px',
          marginLeft: 'auto',
          height: '34px',
          textTransform: 'none',
          '&:hover': {
            backgroundColor: '#4766fc',
          },
          '&:disabled': {
            color: 'white',
          },
          opacity: () => (isTextEmpty() ? 0.5 : 1),
        }}
        disabled={disabled || isTextEmpty() || isUploadingAttachment}
      >
        <FormattedMessage defaultMessage='Send' />
      </Button>
    </Stack>
  );

  return (
    <Box
      sx={{
        width: '100%',
        borderRadius: '6px',
        border: '1px solid #DADADA',
        '&:focus-within': {
          border: '1px solid #181818',
        },
      }}
    >
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        sx={{
          padding: '4px 12px',
          backgroundColor: '#F6F6F6',
          borderRadius: '6px 6px 0px 0px',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Schibsted Grotesk',
            color: '#878787',
          }}
        ></Typography>
        <ExpandButton handleButtonClicked={handleOpenDialog} variant='dark' />
      </Stack>
      <DescriptionDialog
        text={text}
        onChangeText={textChangeHandler}
        open={openDialog}
        setClose={handleCloseDialog}
        variant='message'
        messageButton={<SendButton />}
        attachedItems={pendingFiles &&
          <PendingAttachments
            pendingFiles={pendingFiles}
            removePendingFile={removePendingFile}
            retryUpload={uploadAllFiles}
            error={isErrorOnUpload}
            fullWidth
          />
        }
        hideTitle
      />
      <StandardTextEditor
        text={text}
        onChangeText={textChangeHandler}
        customPlaceholder={intl.formatMessage({
          defaultMessage: 'Type here...',
        })}
        variant='message'
        messageButton={<SendButton />}
        attachedItems={pendingFiles &&
          <PendingAttachments
            pendingFiles={pendingFiles}
            removePendingFile={removePendingFile}
            retryUpload={uploadAllFiles}
            error={isErrorOnUpload}
          />
        }
      />
    </Box>
  );
};

export default Chatbox;
